import type { WidgetConfig, Urgency } from './config';

export interface SubmitPayload {
  title: string;
  description: string | null;
  urgency: Urgency;
  reporterEmail: string | null;
  reporterName: string | null;
  context: {
    url: string;
    userAgent: string;
    viewport: { width: number; height: number };
    consoleErrors: string[];
    referrer: string | null;
  };
}

export interface SubmitResult {
  ok: boolean;
  requestNumber?: string;
  errorMessage?: string;
  status?: number;
}

export interface AttachmentUploadResult {
  ok: boolean;
  errorMessage?: string;
  status?: number;
}

// Hard ceiling on submitRequest. Without this, a hung request leaves the
// widget UI stuck in "Sending…" state forever — subsequent clicks bounce
// off `state.submitting === true` and look like the Send button is dead.
const SUBMIT_TIMEOUT_MS = 15_000;

export async function submitRequest(config: WidgetConfig, payload: SubmitPayload): Promise<SubmitResult> {
  const url = `${config.apiUrl}/api/public/v2/widget/requests`;
  const body = {
    clientId: config.clientId,
    // Sent only when configured. Backend gates linking on the Agency plan
    // and validates the engagement belongs to the workspace + client — we
    // forward unconditionally and let the server enforce.
    ...(config.engagementId ? { engagementId: config.engagementId } : {}),
    title: payload.title,
    description: payload.description,
    urgency: payload.urgency,
    reporterEmail: payload.reporterEmail,
    reporterName: payload.reporterName,
    context: payload.context,
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': config.apiKey,
      },
      body: JSON.stringify(body),
      credentials: 'omit',
      mode: 'cors',
      signal: controller.signal,
    });

    if (res.status === 201) {
      const json = await res.json().catch(() => null) as { data?: { requestNumber?: string } } | null;
      return { ok: true, requestNumber: json?.data?.requestNumber, status: 201 };
    }

    let message = 'Submission failed';
    try {
      const json = await res.json() as { message?: string; error?: string };
      message = json.message ?? json.error ?? message;
    } catch { /* keep default */ }

    if (res.status === 429) message = "You're sending these too quickly. Please try again in a moment.";
    if (res.status === 403) message = "This page isn't authorized to send requests. Contact support.";
    if (res.status === 401) message = 'Widget configuration error. Please contact the site admin.';

    return { ok: false, errorMessage: message, status: res.status };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { ok: false, errorMessage: 'Request timed out. Please try again.' };
    }
    return {
      ok: false,
      errorMessage: 'Network error — please check your connection and try again.',
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Upload one attachment (screenshot, recording, or user-picked file) to a
 * request the widget just created. Soft-fails — a failed attachment doesn't
 * cancel the request itself; the UI surfaces a per-item error so the user
 * can retry just that one.
 */
export async function uploadAttachment(
  config: WidgetConfig,
  requestNumber: string,
  file: Blob,
  filename: string
): Promise<AttachmentUploadResult> {
  const url = `${config.apiUrl}/api/public/v2/widget/requests/${encodeURIComponent(requestNumber)}/attachments`;
  const form = new FormData();
  // The backend expects field name "files" (matches the existing multer config)
  form.append('files', file, filename);

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'X-API-Key': config.apiKey },
      body: form,
      credentials: 'omit',
      mode: 'cors',
    });

    if (res.status === 201) return { ok: true, status: 201 };

    let message = 'Attachment upload failed';
    try {
      const json = await res.json() as { message?: string; error?: string };
      message = json.message ?? json.error ?? message;
    } catch { /* keep default */ }
    if (res.status === 403) message = 'Storage is full — please contact support.';
    if (res.status === 413) message = 'File is too large.';
    if (res.status === 429) message = 'Too many uploads — try again in a moment.';
    return { ok: false, errorMessage: message, status: res.status };
  } catch (err) {
    return { ok: false, errorMessage: 'Upload failed — network error.' };
  }
}

/**
 * Upload one voice note (FR-2026-0105) to the request the widget just
 * created. The server applies the comment voice-note rules (FR-2026-0061):
 * audio type checked against the bytes, size cap and measured length.
 */
export async function uploadVoiceNote(
  config: WidgetConfig,
  requestNumber: string,
  blob: Blob,
  durationMs: number,
): Promise<AttachmentUploadResult> {
  const url = `${config.apiUrl}/api/public/v2/widget/requests/${encodeURIComponent(requestNumber)}/voice-notes`;
  const ext = /mp4/.test(blob.type) ? 'm4a' : /ogg/.test(blob.type) ? 'ogg' : /mpeg/.test(blob.type) ? 'mp3' : 'webm';
  const form = new FormData();
  form.append('durationMs', String(Math.round(durationMs)));
  form.append('voice', blob, `voice-note.${ext}`);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'X-API-Key': config.apiKey },
      body: form,
      credentials: 'omit',
      mode: 'cors',
    });
    if (res.status === 201) return { ok: true, status: 201 };
    let message = 'Voice note upload failed';
    try {
      const json = await res.json() as { message?: string; error?: string };
      message = json.message ?? json.error ?? message;
    } catch { /* keep default */ }
    if (res.status === 429) message = 'Too many uploads. Try again in a moment.';
    return { ok: false, errorMessage: message, status: res.status };
  } catch {
    return { ok: false, errorMessage: 'Voice note upload failed: network error.' };
  }
}
